import dagre from 'dagre';

const NODE_WIDTH = 200;
const NODE_HEIGHT = 56;

type LineageNodeType = 'dataset' | 'job' | 'prompt' | 'agent';

interface ApiNode {
  node_id?: string | number | null;
  id?: string | number | null;
  name: string;
  namespace?: string | null;
  node_type?: string;
}

interface ApiEdge {
  source_node_id?: string | number | null;
  source?: string | number | null;
  target_node_id?: string | number | null;
  target?: string | number | null;
}

interface ApiGraph {
  nodes?: ApiNode[];
  edges?: ApiEdge[];
  focus_node_id?: string | number | null;
}

interface FlowNodeData extends Record<string, unknown> {
  label: string;
  namespace?: string;
  nodeType?: string;
  isFocus: boolean;
}

interface FlowNode {
  id: string;
  type: LineageNodeType;
  position: { x: number; y: number };
  data: FlowNodeData;
  [key: string]: unknown;
}

interface FlowEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  markerEnd: { type: string };
  [key: string]: unknown;
}

/**
 * Get stable node id from API or mock node (backend uses node_id, mock may use id).
 */
function getNodeId(node: ApiNode): string {
  const raw = node.node_id ?? node.id;
  return raw == null ? '' : String(raw);
}

/**
 * Get source/target from edge (backend: source_node_id/target_node_id, mock: source/target).
 */
function getEdgeEndpoints(edge: ApiEdge): { sourceId: string; targetId: string } {
  const source = edge.source_node_id ?? edge.source;
  const target = edge.target_node_id ?? edge.target;
  return {
    sourceId: source == null ? '' : String(source),
    targetId: target == null ? '' : String(target),
  };
}

/**
 * Transform backend/mock lineage graph into React Flow nodes and edges with dagre layout.
 */
export function toFlowGraph(
  apiGraph: ApiGraph | null | undefined,
  focusNodeId: string | number | null = null
): { nodes: FlowNode[]; edges: FlowEdge[] } {
  const nodes = apiGraph?.nodes ?? [];
  const edges = apiGraph?.edges ?? [];
  const focusId = focusNodeId == null ? null : String(focusNodeId);

  const nodeIdSet = new Set(nodes.map(getNodeId).filter(Boolean));

  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: 'TB',
    nodesep: 60,
    ranksep: 80,
    marginx: 20,
    marginy: 20,
  });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((node) => {
    const id = getNodeId(node);
    if (!id) return;
    g.setNode(id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    const { sourceId, targetId } = getEdgeEndpoints(edge);
    if (
      sourceId &&
      targetId &&
      nodeIdSet.has(sourceId) &&
      nodeIdSet.has(targetId)
    ) {
      g.setEdge(sourceId, targetId);
    }
  });

  dagre.layout(g);

  const KNOWN_TYPES = new Set<LineageNodeType>(['job', 'prompt', 'agent']);

  const flowNodes = nodes
    .map((node): FlowNode | null => {
      const id = getNodeId(node);
      if (!id) return null;
      const dagreNode = g.node(id);
      const position = dagreNode
        ? {
            x: dagreNode.x - NODE_WIDTH / 2,
            y: dagreNode.y - NODE_HEIGHT / 2,
          }
        : { x: 0, y: 0 };

      const nodeType: LineageNodeType =
        node.node_type && KNOWN_TYPES.has(node.node_type as LineageNodeType)
          ? (node.node_type as LineageNodeType)
          : 'dataset';

      return {
        id,
        type: nodeType,
        position,
        data: {
          label: node.name,
          namespace: node.namespace ?? undefined,
          nodeType: node.node_type,
          isFocus: focusId !== null && id === focusId,
        },
      };
    })
    .filter((n): n is FlowNode => n !== null);

  const flowEdges = edges
    .map((edge, index): FlowEdge | null => {
      const { sourceId, targetId } = getEdgeEndpoints(edge);
      if (
        !sourceId ||
        !targetId ||
        !nodeIdSet.has(sourceId) ||
        !nodeIdSet.has(targetId)
      ) {
        return null;
      }
      return {
        id: `e-${sourceId}-${targetId}-${index}`,
        source: sourceId,
        target: targetId,
        type: 'smoothstep',
        markerEnd: { type: 'arrowclosed' },
      };
    })
    .filter((e): e is FlowEdge => e !== null);

  return { nodes: flowNodes, edges: flowEdges };
}
